// OfferOptInModal — Phase 15.5 agent self opt-in + sub-agent rate
// viewer. Dark luxury theme, glass-morphism, vibrant cyan/orange
// accents. Mobile-first with internal scroll + sticky footer.
//
// Phase R8 (2026-05-14): the sub-agent rate EDITOR has been removed.
// Agents can still see their direct sub-agents' offer_reward rates
// (transparency on downline economics) but the rates are now set
// globally by admins per hierarchy level. The Phase 15.3 mutation
// endpoint (.../partnership/children/:id/apply) returns 403; this
// modal no longer calls it.
//
// Two concerns remain in one modal:
//   1. Opt-in toggle (POST/DELETE /admin/me-agent/offers/:id/optin)
//   2. Exposure context (the agent's snapshot rate, max possible
//      spend, spent so far) PLUS a read-only list of direct sub-agents
//      with their current offer_reward/global rate displayed.

import { useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type OfferLite = {
  id: number;
  title: string;
  image_url: string | null;
  reward_kind: 'cash' | 'percent' | 'item';
  reward_value: number;
  reward_cap: number | null;
};

type Exposure = {
  offer: {
    id: number; title: string; description: string | null;
    image_url: string | null; reward_kind: string; reward_value: number;
    reward_cap: number | null; global_budget: number | null;
    budget_used: number;
  };
  me: {
    currentRate: number;
    snapshotPct: number | null;
    optedIn: boolean;
    optedInAt: string | null;
    childCount: number;
  };
  exposure: {
    spentSoFar: number;
    remainingBudget: number | null;
    maxExposure: number | null;
    // 2026-05-20 — Transparency itemization. Per-claim breakdown of
    // every deduction this agent has paid for this offer. Backend ships
    // up to 200 newest rows; `spentSoFar` remains the true lifetime
    // total.
    claims?: Array<{
      splitId: number;
      claimId: number;
      userId: number;
      playerName: string;
      playerUsername: string | null;
      triggerKind: string | null;
      triggerRef: string | null;
      claimTotal: number;
      agentDeducted: number;
      ratePctSnapshot: number | null;
      sharePct: number | null;
      wasOverdraft: boolean;
      claimState: string;
      claimedAt: string;
    }>;
  };
};

type ChildRow = {
  id: number;
  username: string;
  level: number | null;
  status: string;
  rates: Array<{ pillar: string; game_id: string; rate_pct: number }>;
};

type ChildrenResponse = {
  children: ChildRow[];
  ceilings: Array<{ pillar: string; game_id: string; ceiling_pct: number }>;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function OfferOptInModal({
  offer, onClose, onUpdated,
}: {
  offer: OfferLite;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const t = useT();
  const { push } = useToast();
  const [exposure, setExposure] = useState<Exposure | null>(null);
  const [children, setChildren] = useState<ChildRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Initial fetch — exposure summary + (if opted in) sub-agent list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<Exposure>(`/admin/me-agent/offers/${offer.id}/exposure`);
        if (cancelled) return;
        setExposure(r);
        if (r.me.optedIn && r.me.childCount > 0) {
          const c = await adminApi.get<ChildrenResponse>('/admin/me-agent/partnership/children');
          if (cancelled) return;
          setChildren(c.children);
        }
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || t('agent.rewards.optInModal.error.loadFailed'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.id]);

  // Ceiling = the agent's own snapshot (if opted in) OR current rate.
  // Shown in the sub-agent panel as context: "your share is X%, here
  // is what each direct sub-agent earns out of that."
  const ceiling = exposure?.me.snapshotPct ?? exposure?.me.currentRate ?? 0;

  async function doOptIn() {
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/me-agent/offers/${offer.id}/optin`);
      push('success', t('agent.rewards.optInModal.toast.optedIn'));
      // Re-fetch everything.
      const r = await adminApi.get<Exposure>(`/admin/me-agent/offers/${offer.id}/exposure`);
      setExposure(r);
      if (r.me.childCount > 0) {
        const c = await adminApi.get<ChildrenResponse>('/admin/me-agent/partnership/children');
        setChildren(c.children);
      }
      onUpdated();
    } catch (e) {
      setErr((e as ApiError).message || t('agent.rewards.optInModal.error.optInFailed'));
    } finally { setBusy(false); }
  }

  async function doOptOut() {
    if (!confirm(t('agent.rewards.optInModal.confirmOptOut'))) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.del(`/admin/me-agent/offers/${offer.id}/optin`);
      push('info', t('agent.rewards.optInModal.toast.optedOut'));
      onUpdated();
      onClose();
    } catch (e) {
      setErr((e as ApiError).message || t('agent.rewards.optInModal.error.optOutFailed'));
    } finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center
                 bg-black/65 backdrop-blur-md p-3 sm:p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* U1 (2026-07-09): tokenized the hardcoded hex gradient. The modal is
          a deliberate dark-luxe surface in BOTH themes (it floats over a
          dimmed backdrop, all inner text is white-on-dark by design) — but
          its colors now come from the theme palette, not magic hexes. */}
      <div
        className="relative w-full max-w-lg max-h-[92dvh] flex flex-col
                   rounded-3xl overflow-hidden border border-white/10
                   bg-gradient-to-br from-arena-950 via-arena-900 to-amber-950
                   shadow-[0_20px_60px_-25px_rgba(0,0,0,0.85)]"
      >
        {/* Background ambient halos — same vocabulary as OfferHero */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-1/3 -right-1/4 w-3/4 h-3/4 bg-brand-500/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/3 -left-1/4 w-2/3 h-2/3 bg-cyan-500/25 rounded-full blur-3xl" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex items-center gap-3 px-5 py-4 border-b border-white/10">
          {offer.image_url ? (
            <div className="relative w-14 h-14 shrink-0 rounded-xl overflow-hidden
                            bg-gradient-to-br from-arena-900 to-black grid place-items-center">
              <img src={offer.image_url} alt="" loading="lazy"
                   className="relative w-[80%] h-[80%] object-contain
                              drop-shadow-[0_2px_3px_rgba(0,0,0,0.20)]" />
            </div>
          ) : (
            <span className="grid place-items-center w-14 h-14 shrink-0 rounded-xl
                             bg-violet-400/15 text-violet-300">
              <Icon name="card_giftcard" size={26} filled />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[.25em] font-bold text-brand-300/95">
              {t('agent.rewards.optInModal.eyebrow')}
            </div>
            <h2 className="font-display font-extrabold text-base sm:text-lg text-white leading-tight line-clamp-2">
              {offer.title}
            </h2>
          </div>
          <button onClick={onClose} aria-label={t('agent.rewards.optInModal.close')}
                  className="text-2xl leading-none text-white/55 hover:text-white">×</button>
        </div>

        {/* Body — scrolls if content overflows */}
        <div className="relative z-10 overflow-y-auto px-5 py-4 space-y-4">
          {err && (
            <div className="rounded-lg bg-rose-500/15 border border-rose-400/40
                            text-rose-200 text-xs px-3 py-2">
              {err}
            </div>
          )}

          {!exposure && !err && (
            <div className="grid place-items-center py-8 text-white/60 text-sm">
              <Icon name="progress_activity" size={28} className="animate-spin" />
              <p className="mt-2">{t('agent.rewards.optInModal.loading')}</p>
            </div>
          )}

          {exposure && (
            <>
              {/* Phase 5 (2026-05-15): Direct Referral Guarantee.
                  This is the headline message of the new model — must
                  be visible BEFORE the stake numbers so the agent
                  frames everything else through this lens. */}
              <DirectReferralGuaranteePanel hasDownline={exposure.me.childCount > 0} />

              {/* Section A — Your stake (renamed/reinterpreted) */}
              <YourStakePanel exposure={exposure} />

              {/* Phase 5 — Upline Reality Check. Only shown to agents
                  with downline. Explains the proportional pool math
                  in their context. */}
              {exposure.me.childCount > 0 && (
                <UplineRealityPanel
                  exposure={exposure}
                  childCount={exposure.me.childCount}
                />
              )}

              {/* Section B — Opt-in toggle */}
              <OptInTogglePanel
                exposure={exposure}
                busy={busy}
                onOptIn={doOptIn}
                onOptOut={doOptOut}
              />

              {/* 2026-05-20 — Per-claim transparency. Shows the agent
                  every individual deduction made from their wallet on
                  this offer: who claimed, when, what kind of trigger,
                  the full claim ₹ + the slice this agent paid.
                  Only renders when there's at least one row to show. */}
              {(exposure.exposure.claims?.length ?? 0) > 0 && (
                <ClaimsItemizationPanel
                  claims={exposure.exposure.claims!}
                  spentSoFar={exposure.exposure.spentSoFar}
                />
              )}

              {/* Section C — Sub-agent rates (READ-ONLY, Phase R8) */}
              {exposure.me.optedIn && exposure.me.childCount > 0 && (
                <SubAgentRatesPanel
                  ceiling={ceiling}
                  children={children}
                />
              )}
              {exposure.me.optedIn && exposure.me.childCount === 0 && (
                <div className="rounded-2xl bg-white/[0.03] border border-white/10
                                px-4 py-3 text-xs text-white/65">
                  {t('agent.rewards.optInModal.subAgents.noChildren')}
                </div>
              )}
            </>
          )}
        </div>

        {/* Sticky footer — Phase R8: Save button retired. Footer holds
            only the Done/Close action since the modal no longer
            mutates downline rates. */}
        {exposure && (
          <div className="relative z-10 flex items-center justify-end gap-2
                          px-5 py-3.5 border-t border-white/10
                          bg-black/30 backdrop-blur">
            <button onClick={onClose} disabled={busy}
                    className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full
                               text-xs font-extrabold uppercase tracking-wider text-white
                               bg-white/10 hover:bg-white/15 border border-white/15
                               active:scale-[0.98] transition disabled:opacity-50">
              {t('agent.rewards.optInModal.cta.done')} <Icon name="check" size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

function YourStakePanel({ exposure }: { exposure: Exposure }) {
  const t = useT();
  const rate = exposure.me.snapshotPct ?? exposure.me.currentRate;
  const isSnapshot = exposure.me.optedIn;
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10
                    px-4 py-3.5 space-y-3">
      <div className="flex items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[.22em] font-bold text-white/55">
            {t('agent.rewards.optInModal.stake.shareLabel')}
          </div>
          <div className="font-display font-black text-3xl sm:text-4xl text-white leading-none
                          drop-shadow-[0_2px_8px_rgba(143,255,254,0.3)]">
            {rate}%
          </div>
        </div>
        <div className="ml-auto text-right">
          {/* Parallel state keys — "Locked at opt-in" vs "Current rate". */}
          <div className="text-[10px] uppercase tracking-[.22em] text-white/55">
            {isSnapshot ? t('agent.rewards.optInModal.stake.locked') : t('agent.rewards.optInModal.stake.current')}
          </div>
          {isSnapshot && exposure.me.optedInAt && (
            <div className="text-[11px] text-white/65 tabular-nums">
              {new Date(exposure.me.optedInAt).toLocaleDateString()}
            </div>
          )}
          {!isSnapshot && exposure.me.currentRate <= 0 && (
            <div className="text-[11px] text-amber-300">
              {t('agent.rewards.optInModal.stake.noRate')}
            </div>
          )}
        </div>
      </div>

      {/* Exposure metrics — each label is paired with a translator-only
          .helper key (in the dict) that anchors the financial meaning
          so "Spend" / "Max possible" don't drift to leisure / lottery
          senses. The helpers are NOT rendered in the data-viz here —
          they exist purely for translator context. */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        <Metric
          label={t('agent.rewards.optInModal.stake.spentSoFar.label')}
          value={inr(exposure.exposure.spentSoFar)}
          tone="neutral"
        />
        <Metric
          label={t('agent.rewards.optInModal.stake.maxPossible.label')}
          value={
            exposure.exposure.maxExposure == null
              ? t('agent.rewards.optInModal.stake.maxPossible.noCap')
              : inr(exposure.exposure.maxExposure)
          }
          tone="warn"
        />
      </div>
      {exposure.exposure.remainingBudget != null && (
        <div className="text-[10.5px] text-white/55 leading-relaxed">
          {t('agent.rewards.optInModal.stake.worstCase', {
            amount: exposure.exposure.remainingBudget.toLocaleString('en-IN'),
            pct: rate,
          })}
        </div>
      )}
    </div>
  );
}

function OptInTogglePanel({
  exposure, busy, onOptIn, onOptOut,
}: {
  exposure: Exposure;
  busy: boolean;
  onOptIn: () => void;
  onOptOut: () => void;
}) {
  const t = useT();
  const optedIn = exposure.me.optedIn;
  return (
    <div className={`rounded-2xl border px-4 py-3.5
                     ${optedIn
                       ? 'bg-emerald-500/10 border-emerald-400/40'
                       : 'bg-white/[0.04] border-white/10'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {/* Parallel title + body keys per opt-in state. Group 7
              directive #2 — no inline ternary on the literal string;
              each state owns its translator-managed sentence. */}
          <div className="text-[11px] uppercase tracking-wider font-bold
                          text-white/80">
            {optedIn
              ? t('agent.rewards.optInModal.toggle.title.optedIn')
              : t('agent.rewards.optInModal.toggle.title.optedOut')}
          </div>
          <div className="text-[11.5px] text-white/65 mt-1 leading-relaxed">
            {optedIn
              ? t('agent.rewards.optInModal.toggle.body.optedIn')
              : t('agent.rewards.optInModal.toggle.body.optedOut')}
          </div>
        </div>
        {/* Toggle pill */}
        <button
          type="button"
          onClick={optedIn ? onOptOut : onOptIn}
          disabled={busy || (!optedIn && exposure.me.currentRate <= 0)}
          aria-pressed={optedIn}
          className={`shrink-0 relative inline-flex items-center
                      h-9 w-16 rounded-full border transition
                      ${optedIn
                        ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 border-emerald-300/50 shadow-[0_0_18px_rgba(16,185,129,0.55)]'
                        : 'bg-white/10 border-white/20 hover:bg-white/15'}
                      disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <span
            className={`absolute top-0.5 h-8 w-8 rounded-full bg-white shadow transition-all
                        ${optedIn ? 'left-7' : 'left-0.5'}`}
          />
        </button>
      </div>
      {optedIn && (
        <button onClick={onOptOut} disabled={busy}
                className="mt-3 text-[11px] text-rose-300 hover:text-rose-200
                           hover:underline">
          {t('agent.rewards.optInModal.toggle.optOutLink')}
        </button>
      )}
    </div>
  );
}

/**
 * SubAgentRatesPanel — Phase R8 (2026-05-14): now read-only.
 *
 * Was an editor that let the parent agent grant per-(offer, child)
 * offer_reward rates. The editor is gone — admins set rates globally
 * by hierarchy level now. The panel survives as a transparency view
 * so the parent can see exactly what each direct sub-agent earns on
 * this offer's pillar (offer_reward / global) at a glance.
 */
function SubAgentRatesPanel({
  ceiling, children,
}: {
  ceiling: number;
  children: ChildRow[] | null;
}) {
  const t = useT();
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10
                    px-4 py-3.5 space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[.22em] font-bold text-white/55">
            {t('agent.rewards.optInModal.subAgents.title')}
          </div>
          <div className="text-[12px] text-white/75 mt-0.5">
            {t('agent.rewards.optInModal.subAgents.ceilingHint.before')}<strong className="text-cyan-300">{t('agent.rewards.optInModal.subAgents.ceilingHint.value', { pct: ceiling })}</strong>
          </div>
        </div>
        {/* Phase R8: read-only badge — rates are admin-set globally */}
        <span className="text-[9.5px] uppercase tracking-wider font-bold
                         text-cyan-300/90 border border-cyan-400/30 rounded
                         px-1.5 py-0.5 bg-cyan-400/10 inline-flex items-center gap-0.5">
          <Icon name="lock" size={9} />
          read only
        </span>
      </div>

      {!children && (
        <div className="grid place-items-center py-4 text-white/55 text-xs">
          <Icon name="progress_activity" size={20} className="animate-spin" />
        </div>
      )}

      {children && children.length === 0 && (
        <div className="text-[11.5px] text-white/55">{t('agent.rewards.optInModal.subAgents.empty')}</div>
      )}

      {children && children.length > 0 && (
        <div className="space-y-2">
          {children.map((ch) => {
            const offerRate = ch.rates.find(
              (rt) => rt.pillar === 'offer_reward' && rt.game_id === 'global'
            );
            const rate = Number(offerRate?.rate_pct ?? 0);
            return (
              <div key={ch.id} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  {/* 2026-05-18 — Identity masking: display_name only. */}
                  <div className="text-[12.5px] font-bold text-white truncate">
                    <span className={(((ch as { display_name?: string }).display_name) || '').trim() ? '' : 'opacity-60'}>
                      {(((ch as { display_name?: string }).display_name) || '').trim() || '—'}
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70">
                    {/* Level hidden from agents (policy 2026-05-25); status only. */}
                    {ch.status}
                  </div>
                </div>
                {/* Phase R8 — was a writable <input>. Now a read-only pill. */}
                <div className="shrink-0">
                  <span className="inline-flex items-baseline justify-end gap-0.5
                                   px-3 py-1.5 rounded-lg
                                   bg-white/[0.06] border border-white/15
                                   text-sm font-bold tabular-nums text-white">
                    {rate}<span className="text-[11px] text-white/55">%</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[10.5px] text-white/45 leading-relaxed">
        Rates are set globally by the platform admin per hierarchy
        level. Shown here for transparency only.
      </div>
    </div>
  );
}

function Metric({ label, value, tone }:
  { label: string; value: string; tone: 'neutral' | 'warn' }) {
  const colour = tone === 'warn' ? 'text-amber-300' : 'text-cyan-200';
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[.18em] text-white/55">
        {label}
      </div>
      <div className={`font-display font-black text-base tabular-nums ${colour}
                       drop-shadow-[0_2px_6px_rgba(0,0,0,0.4)]`}>
        {value}
      </div>
    </div>
  );
}

/**
 * ClaimsItemizationPanel — 2026-05-20.
 *
 * The transparency requirement: every ₹ that left this agent's wallet
 * for this offer is itemized. Per row the agent sees the player who
 * claimed, what kind of trigger fired the offer (signup / cashback /
 * daily / etc.), the gross claim amount that the platform credited
 * the player, and the slice the agent themselves paid. Overdraft rows
 * (deposit cushion exhausted) get a red dot so a routine glance
 * surfaces concerning patterns.
 *
 * Sorted newest-first by the backend; capped at 200 rows. The header
 * total mirrors the `Spent so far` metric in YourStakePanel — they
 * agree by construction (same SUM, just displayed here as a header
 * for the table).
 */
function ClaimsItemizationPanel({
  claims, spentSoFar,
}: {
  claims: NonNullable<Exposure['exposure']['claims']>;
  spentSoFar: number;
}) {
  const triggerLabel = (k: string | null): string => {
    if (!k) return '—';
    // Keep this map close to triggerKindFor() in offerEngine.js so the
    // agent's display matches what the backend writes.
    const map: Record<string, string> = {
      signup:       'Signup',
      daily:        'Daily check-in',
      announcement: 'Announcement',
      recharge:     'Recharge',
      cashback:     'Cashback',
      win_streak:   'Win streak',
      challenge:    'Game challenge',
      play_streak:  'Play streak',
      time_played:  'Time played',
      spend:        'Wallet spend',
    };
    return map[k] || k;
  };
  const fmtDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
           + ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10
                    px-4 py-3.5 space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[.22em] font-bold text-white/55">
            Reward Deductions
          </div>
          <div className="text-[12px] text-white/75 mt-0.5">
            {claims.length} {claims.length === 1 ? 'claim' : 'claims'} ·
            {' '}<strong className="text-cyan-300">{inr(spentSoFar)}</strong>
            {' '}total deducted from your wallet
          </div>
        </div>
        <span className="text-[9.5px] uppercase tracking-wider font-bold
                         text-cyan-300/90 border border-cyan-400/30 rounded
                         px-1.5 py-0.5 bg-cyan-400/10 inline-flex items-center gap-0.5">
          <Icon name="receipt_long" size={9} />
          itemized
        </span>
      </div>

      {/* Scrollable list; capped height so the modal body stays usable
          on phones. Each row is a 2-line layout: top = player + when,
          bottom = trigger pill + amounts. */}
      <ul className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-1.5">
        {claims.map((c) => (
          <li key={c.splitId}
              className="rounded-lg bg-white/[0.03] border border-white/10
                         px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-bold text-white truncate">
                  {c.playerName}
                  {c.wasOverdraft && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5
                                     align-middle text-[9px] uppercase tracking-wider
                                     font-bold text-rose-200
                                     bg-rose-500/20 border border-rose-400/40
                                     rounded px-1 py-[1px]">
                      <span className="w-1 h-1 rounded-full bg-rose-300" />
                      overdraft
                    </span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">
                  {fmtDate(c.claimedAt)}
                  {c.claimState !== 'credited' && (
                    <span className="ml-1.5 text-amber-300/90">· {c.claimState}</span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[14px] font-black tabular-nums text-rose-300">
                  −{inr(c.agentDeducted)}
                </div>
                <div className="text-[10px] tabular-nums text-white/45">
                  of {inr(c.claimTotal)} total
                </div>
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10.5px]
                               text-white/70 bg-white/[0.05]
                               border border-white/10 rounded px-1.5 py-0.5">
                <Icon name="bolt" size={10} className="text-amber-300" />
                {triggerLabel(c.triggerKind)}
              </span>
              {c.ratePctSnapshot != null && (
                <span className="text-[10px] tabular-nums text-white/55">
                  rate {c.ratePctSnapshot}%
                </span>
              )}
              {c.sharePct != null && c.sharePct !== c.ratePctSnapshot && (
                <span className="text-[10px] tabular-nums text-white/45">
                  · share {c.sharePct}%
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Phase 5 (2026-05-15): transparency panels ──────────────────────

/**
 * DirectReferralGuaranteePanel — the headline message of the Phase 5
 * model. Direct referrers pay ₹0; uplines + admin subsidize.
 *
 * Shows different framing based on whether the viewing agent has any
 * downline. Direct-only agents see the "you pay nothing" framing.
 * Agents with downline see "your downline players are protected" so
 * they understand the burden on them but in the context of helping
 * their network grow.
 */
function DirectReferralGuaranteePanel({ hasDownline }: { hasDownline: boolean }) {
  const t = useT();
  // Translation strings contain inline <strong> markup so the bold
  // phrases survive across all 12 languages. dangerouslySetInnerHTML
  // is safe here because the content is from our own static
  // translation dictionary, never user input.
  const bodyKey = hasDownline
    ? 'agent.rewards.optInModal.guarantee.body.withDownline'
    : 'agent.rewards.optInModal.guarantee.body.noDownline';
  return (
    <div className="rounded-2xl border border-emerald-400/50
                    bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent
                    px-4 py-3.5 space-y-2">
      <div className="flex items-center gap-2">
        <Icon name="verified" size={18} className="text-emerald-300" filled />
        <div className="text-[10px] uppercase tracking-[.22em] font-extrabold text-emerald-200">
          {t('agent.rewards.optInModal.guarantee.title')}
        </div>
      </div>
      <p className="text-[13px] text-white leading-relaxed"
         dangerouslySetInnerHTML={{ __html: t(bodyKey) }} />
    </div>
  );
}

/**
 * UplineRealityPanel — the second message for agents with downline.
 *
 * Sets expectations: "If a SUB-agent's player claims this offer, you
 * pay a proportional share based on your Pool Weight compared to the
 * rest of the active upline." This prevents the "I thought I was
 * protected and now I'm being charged" surprise.
 *
 * Uses the agent's snapshotPct/currentRate as their weight — the same
 * value creditClaim.js reads to compute their proportional share.
 */
function UplineRealityPanel({
  exposure, childCount,
}: { exposure: Exposure; childCount: number }) {
  const t = useT();
  const weight = exposure.me.snapshotPct ?? exposure.me.currentRate ?? 0;
  // The translated body uses {count}, {plural}, {weight} placeholders.
  // Each language picks its own plural marker (most Indic languages
  // don't need a separate 's', so plural is generally empty for those).
  return (
    <div className="rounded-2xl border border-amber-400/40
                    bg-amber-500/10 px-4 py-3 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Icon name="hub" size={14} className="text-amber-300" />
        <div className="text-[10px] uppercase tracking-[.22em] font-bold text-amber-200">
          {t('agent.rewards.optInModal.uplineReality.title')}
        </div>
      </div>
      <p className="text-[12px] text-amber-50/90 leading-relaxed"
         dangerouslySetInnerHTML={{ __html: t('agent.rewards.optInModal.uplineReality.body', {
           count: childCount,
           plural: childCount === 1 ? '' : 's',
           weight,
         }) }} />
    </div>
  );
}
